import { FormCloseIcon, FormEditIcon, MapIcon, PlusIconBlue } from 'assets/icons';
import { ADDRESS_TYPE } from 'containers/customers/constants';
import AddressModal from 'containers/jobs/components/AddressModal';
import { useAppDispatch, useAppSelector } from 'hooks';
import React, { FC, useEffect, useRef, useState } from 'react';
import {
  removeJobValidationErrors,
  selectJobCustomer,
  selectJobValidationErrors,
  selectServiceAddress,
  selectServiceArea,
  updateAssignableTeamMembers,
  updateAssigneeList,
  updateServiceAddress,
  updateServiceArea,
  updateServiceAreaAssignees
} from '../createJobSlice';
import { AddCustomerAddressPayload, CustomerAddress } from 'containers/customers/customers.model';
import { useAddCustomerAddressMutation, useEditCustomerAddressMutation } from 'containers/customers/api';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { AddressField } from 'containers/invoices/invoices.model';
import { getAddressAsString, getJobValidationErrors } from 'containers/jobs/utils';
import { CreateJobErrorTypes } from 'containers/jobs/constant';
import { selectAutoFilledData } from '../createJobSlice';
import AutoFilledIndicator from './AutoFilledIndicator';
import { AssignableMember, ServiceAreaInfo } from 'containers/jobs/jobs.model';
import ServiceAreaDropdown from './ServiceAreaDropdown';
import { useFindServiceAreasMutation, useLazyGetAssignableMembersQuery } from 'containers/jobs/api';
import { If } from 'components';
import { useCreateServiceAreaMutation, useGetServiceAreaByIdQuery } from 'containers/settings/service-areas/api';
import { ServiceAreaType } from 'containers/settings/service-areas/constants';
import {
  CreateServiceAreaFormData,
  CreateServiceAreaPayload
} from 'containers/settings/service-areas/serviceAreas.model';
import { selectIsFieldServePlus } from 'containers/subscriptions/modalSlice';

interface ServiceAddressSectionProps {
  className?: string;
  isEditable: boolean;
}

const ServiceAddressSection: FC<ServiceAddressSectionProps> = ({ isEditable }) => {
  const [openServiceAddressModal, setOpenServiceAddressModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedServiceArea, setSelectedServiceArea] = useState<ServiceAreaInfo | null>(null);
  const [formattedAreas, setFormattedAreas] = useState<ServiceAreaInfo[]>([]);
  const [findServiceAreas, { isLoading: isLoadingServiceAreas }] = useFindServiceAreasMutation();
  const previousCoordinates = useRef<[number, number] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [
    addCustomerAddressMutation,
    { isSuccess: customerAddressSuccess, data: customerAddress, isLoading: loadingCustomerAddressAPI }
  ] = useAddCustomerAddressMutation();
  const [
    editCustomerAddressMutation,
    { isSuccess: isEditAddressSuccess, data: editAddressData, isLoading: loadingEditAddressAPI }
  ] = useEditCustomerAddressMutation();
  const [createServiceArea, { isLoading: isLoadingCreateServiceArea }] = useCreateServiceAreaMutation();

  const dispatch = useAppDispatch();

  const serviceAddress: CustomerAddress | AddressField = useAppSelector(selectServiceAddress);
  const currentJobCustomer = useAppSelector(selectJobCustomer);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const selectedJobValidations = useAppSelector(selectJobValidationErrors);
  const existingServiceArea = useAppSelector(selectServiceArea);
  const autoFilledData = useAppSelector(selectAutoFilledData);
  const isFieldServePlus = useAppSelector(selectIsFieldServePlus);

  const { data: serviceAreaDetails } = useGetServiceAreaByIdQuery(
    {
      teamId: currentTeam?.id?.toString() || '',
      serviceAreaId: selectedServiceArea?.serviceAreaId || ''
    },
    {
      skip: !selectedServiceArea?.serviceAreaId || !currentTeam?.id,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: false,
      pollingInterval: 0
    }
  );

  const [getAssignableTeamMembers, { data: assignableTeamMembersData }] = useLazyGetAssignableMembersQuery();

  useEffect(() => {
    if (customerAddressSuccess) {
      dispatch(removeJobValidationErrors(CreateJobErrorTypes.SERVICE_ADDRESS));
      dispatch(updateServiceAddress(customerAddress));
      closeServiceAddressModal();
    }
  }, [customerAddressSuccess, loadingCustomerAddressAPI]);

  useEffect(() => {
    if (isEditAddressSuccess) {
      dispatch(removeJobValidationErrors(CreateJobErrorTypes.SERVICE_ADDRESS));
      dispatch(updateServiceAddress(editAddressData));
      closeServiceAddressModal();
    }
  }, [isEditAddressSuccess, loadingEditAddressAPI]);

  useEffect(() => {
    if (currentTeam?.id) {
      getAssignableTeamMembers(currentTeam.id.toString());
    }
  }, [currentTeam?.id, getAssignableTeamMembers]);

  useEffect(() => {
    if (serviceAddress && currentTeam?.id) {
      const currentCoordinates: [number, number] = [serviceAddress.latitude, serviceAddress.longitude];

      // Ensure we have valid coordinates
      if (!(serviceAddress.latitude?.toString() && serviceAddress.longitude?.toString())) {
        return;
      }
      const hasCoordinatesChanged =
        !previousCoordinates.current ||
        Math.abs(previousCoordinates.current[0] - currentCoordinates[0]) > 0.000001 ||
        Math.abs(previousCoordinates.current[1] - currentCoordinates[1]) > 0.000001;

      if (hasCoordinatesChanged) {
        // If coordinates have changed and we had previous coordinates, this is an edit
        const isAddressEdited = previousCoordinates.current !== null;
        previousCoordinates.current = currentCoordinates;

        const fetchServiceAreas = async () => {
          setIsLoading(true);
          try {
            const data = await findServiceAreas({
              teamId: currentTeam.id,
              serviceAddressPoint: currentCoordinates
            }).unwrap();

            const newFormattedAreas: ServiceAreaInfo[] = [
              ...(data.enclosingServiceAreas || []),
              ...(data.nearestServiceAreas || []),
              ...(data.defaultServiceAreas || [])
            ];
            setFormattedAreas(newFormattedAreas);

            // If this is an edit (coordinates changed from previous value), always default to first area
            if (isAddressEdited) {
              const defaultArea = newFormattedAreas[0] || null;
              setSelectedServiceArea(defaultArea);
              dispatch(updateServiceArea(defaultArea));
            } else {
              // Only try to find existing area if this is not an edit
              if (existingServiceArea?.serviceAreaId) {
                const existingArea = newFormattedAreas?.find(
                  area => area.serviceAreaId === existingServiceArea.serviceAreaId
                );
                if (existingArea) {
                  setSelectedServiceArea(existingArea);
                } else {
                  const defaultArea = newFormattedAreas[0] || null;
                  setSelectedServiceArea(defaultArea);
                  dispatch(updateServiceArea(defaultArea));
                }
              } else {
                const defaultArea = newFormattedAreas[0] || null;
                setSelectedServiceArea(defaultArea);
                dispatch(updateServiceArea(defaultArea));
              }
            }
          } catch (error) {
            // Error handling is already handled by the API mutation
          } finally {
            setIsLoading(false);
          }
        };

        fetchServiceAreas();
      }
    }
  }, [serviceAddress, currentTeam?.id, existingServiceArea?.serviceAreaId, findServiceAreas, dispatch]);

  useEffect(() => {
    return () => {
      if (selectedServiceArea?.serviceAreaId) {
        dispatch(updateServiceAreaAssignees([]));
      }
    };
  }, [selectedServiceArea?.serviceAreaId]);

  useEffect(() => {
    if (serviceAreaDetails?.assignees) {
      const formattedAssignees: AssignableMember[] = serviceAreaDetails.assignees.map(assignee => ({
        merchantId: assignee.id,
        firstName: assignee.name.split(' ')[0],
        lastName: assignee.name.split(' ').slice(1).join(' '),
        email: assignee.email || '',
        phone: assignee.phone || '',
        id: assignee.id,
        profilePictureUrl: assignee.profilePictureURL || '',
        colorCode: '',
        name: assignee.name
      }));
      dispatch(updateServiceAreaAssignees(formattedAssignees));
    }
  }, [serviceAreaDetails]);

  useEffect(() => {
    if (assignableTeamMembersData?.data?.teamMemberList) {
      // Only use assignable team members when no service areas are available
      if (formattedAreas?.length === 0) {
        const formattedMembers: AssignableMember[] = assignableTeamMembersData.data.teamMemberList?.map(member => ({
          merchantId: member.merchantId,
          email: member.email || '',
          phone: member.phone || '',
          id: member.merchantId,
          profilePictureUrl: member.profilePictureUrl || '',
          colorCode: member.colorCode || '',
          name: member.name || ''
        }));
        dispatch(updateAssignableTeamMembers(formattedMembers));
      } else {
        // Clear assignable team members when service areas are available
        dispatch(updateAssignableTeamMembers([]));
      }
    }
  }, [assignableTeamMembersData, formattedAreas.length, dispatch]);

  const handleOpenServiceFeeModal = () => {
    setOpenServiceAddressModal(true);
  };

  const closeServiceAddressModal = () => {
    setOpenServiceAddressModal(false);
    setIsEdit(false);
  };

  const onAddressAddition = addressDetails => {
    const addressPayload: AddCustomerAddressPayload = {
      ...addressDetails,
      teamId: currentTeam.id.toString(),
      customerId: currentJobCustomer['ID'],
      isBillingSameAsService: false,
      addressId: 0
    };
    if (
      (autoFilledData?.customerLatest && !autoFilledData?.customerLatest?.id) ||
      (autoFilledData?.customerLatest?.serviceAddress && !autoFilledData?.customerLatest?.serviceAddress[0]?.id)
    ) {
      dispatch(updateServiceAddress(addressDetails));
      dispatch(removeJobValidationErrors(CreateJobErrorTypes.SERVICE_ADDRESS));
      closeServiceAddressModal();
    } else {
      if (isEdit) {
        editCustomerAddressMutation({ id: serviceAddress.id.toString(), body: addressPayload });
      } else {
        addCustomerAddressMutation(addressPayload);
      }
    }
  };

  const handleRemoveServiceAddressFromJob = () => {
    dispatch(updateServiceAddress(null));
    dispatch(updateServiceArea(null));
    setSelectedServiceArea(null);
    previousCoordinates.current = null;
  };

  const handleEditServiceAddress = () => {
    setIsEdit(true);
    setOpenServiceAddressModal(true);
  };

  const handleSelectServiceArea = (area: ServiceAreaInfo) => {
    dispatch(updateAssigneeList([]));
    setSelectedServiceArea(area);
    dispatch(updateServiceArea(area));
    dispatch(updateServiceAreaAssignees([]));
  };

  const handleCreateServiceArea = async (data: CreateServiceAreaFormData) => {
    if (!currentTeam?.id || !serviceAddress) return;

    setIsLoading(true);
    try {
      const payload: CreateServiceAreaPayload = {
        teamId: currentTeam.id.toString(),
        name: data.name,
        colorCode: data.color,
        type: data.shape.type,
        assignees: data.members.map(member => member.merchantId),
        ...(data.shape.type === ServiceAreaType.POLYGON
          ? { vertices: data.shape.data as [number, number][] }
          : {
              centroid: (data.shape.data as { centroid: [number, number]; range: number }).centroid,
              range: (data.shape.data as { centroid: [number, number]; range: number }).range
            })
      };

      const response = await createServiceArea(payload).unwrap();

      // Refresh service areas after creation
      if (response) {
        const newData = await findServiceAreas({
          teamId: currentTeam.id,
          serviceAddressPoint: [serviceAddress.latitude, serviceAddress.longitude]
        }).unwrap();

        const newFormattedAreas: ServiceAreaInfo[] = [
          ...(newData.enclosingServiceAreas || []),
          ...(newData.nearestServiceAreas || []),
          ...(newData.defaultServiceAreas || [])
        ];
        setFormattedAreas(newFormattedAreas);
      }
    } catch (error) {
      // Error handling is already handled by the API mutation
    } finally {
      setIsLoading(false);
    }
  };

  const serviceAddressError = getJobValidationErrors(selectedJobValidations, CreateJobErrorTypes.SERVICE_ADDRESS);

  return (
    <div className="relative">
      <div className="pb-2.5 text-17px font-semibold text-heading">
        Service address{' '}
        <AutoFilledIndicator
          isAutoFilled={
            autoFilledData?.customerLatest?.serviceAddress &&
            autoFilledData?.customerLatest?.serviceAddress[0]?.id === serviceAddress?.id
          }
        />
      </div>
      {serviceAddressError && <div className="mb-2.5 text-sbase font-semibold text-error">{serviceAddressError}</div>}
      <div className="relative rounded border border-borderGray p-5">
        {serviceAddress ? (
          <div className="flex items-start gap-1">
            <MapIcon />
            <div>
              <div className="text-sbase font-semibold text-primaryText">
                {getAddressAsString(serviceAddress) ?? '-'}
              </div>
              {serviceAddress.addressNote && (
                <div className="mt-1 text-sbase font-semibold text-primaryText">{serviceAddress.addressNote}</div>
              )}
            </div>
          </div>
        ) : (
          <div
            className="flex cursor-pointer items-center gap-1"
            onClick={handleOpenServiceFeeModal}
            role="presentation">
            <PlusIconBlue />
            <div className="text-17px font-semibold text-secondary">Add a service address</div>
          </div>
        )}
        {serviceAddress && isEditable && (
          <div className="absolute top-1/2 -right-6 -translate-y-1/2 space-y-1.5">
            <FormCloseIcon className="cursor-pointer" onClick={handleRemoveServiceAddressFromJob} />
            <FormEditIcon className="cursor-pointer" onClick={handleEditServiceAddress} />
          </div>
        )}
      </div>
      <If condition={isFieldServePlus && !!serviceAddress}>
        <div className="mt-6">
          <div className="pb-2.5 text-17px font-semibold text-heading">Service area</div>
          <ServiceAreaDropdown
            serviceAreas={formattedAreas}
            selectedArea={selectedServiceArea}
            onSelectArea={handleSelectServiceArea}
            isLoading={isLoadingServiceAreas || isLoading}
            onCreateServiceArea={handleCreateServiceArea}
            isLoadingCreateServiceArea={isLoadingCreateServiceArea || isLoading}
            isLatLngZero={serviceAddress?.latitude === 0 && serviceAddress?.longitude === 0}
          />
        </div>
      </If>
      {openServiceAddressModal && (
        <AddressModal
          onClose={closeServiceAddressModal}
          type={ADDRESS_TYPE.SERVICE}
          onAddAddress={onAddressAddition}
          isEdit={isEdit}
          initialAddress={serviceAddress}
          isLoading={loadingCustomerAddressAPI || loadingEditAddressAPI || isLoading}
        />
      )}
    </div>
  );
};

export default ServiceAddressSection;
